// @flow
import React from 'react';
import { Box, Button, Flex } from '@locus-taxy/neo-ui';
import { translate, type TFunction } from 'react-i18next';

import TimeUtils, { DateTimeFormat } from 'common/utils/TimeUtils';
import Input from 'common/components/core/input';
import { DivWithPopover } from 'common/components/core/wrappers/Div';
import {
    PopoverTriggerEnum,
    PopoverPlaceEnum
} from 'common/components/core/popover/popOverFactory';
import DateTimeRangeSelector from 'common/components/common/filters/DateTimeRangeSelector';
import theme from 'common/entity/orderMaster/DateTimeRangeFilter.scss';
type Props = {
    t: TFunction,
    value: ?{ min: string, max: string },
    onChange: (?{ min: string, max: string }) => void,
    showTime: boolean
};

type FilterValueType = {
    min: string,
    max: string
};

type State = {
    filterValue: FilterValueType,
    error: ?boolean
};

class DateTimeRangeFilter extends React.PureComponent<Props, State> {
    filterPopoverRef: Object;
    constructor(props) {
        super(props);
        this.state = {
            filterValue: {},
            error: false
        };
    }
    getFilterPopoverRef = ref => {
        if (!ref) {
            return null;
        }
        this.filterPopoverRef = ref;
    };

    hideFilterPopover = () => {
        if (this.filterPopoverRef) {
            this.filterPopoverRef.getWrappedInstance().hidePopover();
        }
    };

    onChange = (value, error) => {
        this.setState({ filterValue: value, error });
    };

    onClear = e => {
        e.stopPropagation();
        const { onChange, value } = this.props;
        value && onChange();
        this.setState({ filterValue: {}, error: null });
        this.hideFilterPopover();
    };

    onApply = () => {
        const { filterValue } = this.state;
        const { onChange } = this.props;
        onChange(filterValue);
        this.hideFilterPopover();
    };

    isFilterDisabled = () => {
        const {
            filterValue: { max, min },
            error
        } = this.state;
        if (error) {
            return true;
        }
        return !(max || min);
    };

    renderDateTimeRangeSelector = () => {
        const { t, id, showTime } = this.props;
        const { filterValue } = this.state;
        const applyFilterDisabled = this.isFilterDisabled();
        const field = {
            headerTextId: id
        };
        return (
            <Box>
                <DateTimeRangeSelector
                    onChange={this.onChange}
                    field={field}
                    value={filterValue}
                    showTime={showTime}
                />
                <Flex justifyContent="flex-end" pt={2}>
                    <Button
                        backgroundColor="transparent"
                        mr={2}
                        onClick={this.onClear}
                        size="small"
                    >
                        {t('translations:clear')}
                    </Button>
                    <Button
                        appearance="solid"
                        palette="primary"
                        size="small"
                        onClick={this.onApply}
                        disabled={applyFilterDisabled}
                    >
                        {t('translations:apply')}
                    </Button>
                </Flex>
            </Box>
        );
    };
    getFilterText = () => {
        const { value } = this.props;
        let filterText = '';
        if (value) {
            const { min, max } = value;
            // TODO: check with OIQ
            filterText = TimeUtils.formatTimeRange(
                min,
                max,
                min,
                true,
                DateTimeFormat.HUMAN_DATE_TIME_FORMAT
            );
        }
        return filterText;
    };
    render() {
        const { t, showTime } = this.props;
        const filterText = this.getFilterText();
        const filterPlaceholder = showTime
            ? t('dateTimeRangeFilter')
            : t('dateRangeFilter');
        return (
            <DivWithPopover
                ref={this.getFilterPopoverRef}
                popOverContent={this.renderDateTimeRangeSelector}
                triggerPopoverOn={PopoverTriggerEnum.Click}
                place={PopoverPlaceEnum.Below}
                invertPopover={false}
                popOverClassName={theme.dateRangePopover}
            >
                <Input
                    placeholder={filterPlaceholder}
                    value={filterText}
                    disabled
                    clearableInput
                    onClearInput={this.onClear}
                />
            </DivWithPopover>
        );
    }
}

const TranslatedDateTimeRangeFilter = translate()(DateTimeRangeFilter);

export default TranslatedDateTimeRangeFilter;
